import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { PageWrapper } from 'ctm-components';
import { pageContext } from 'ctm-util-functions';

/**
 * Get the complete set of client side routes. Each route renders the PageWrapper with the props for the route and the
 * page it represents. Although these are 'client' side routes, they are still used by the App component on the server to
 * render the initial requested page.
 * IMPORTANT: The PageWrapper (and therefore the Page) is only mounted when the first route is rendered. It is then UPDATED
 * on each route change. This why every route has the same key. This logic is important as it saves a lot of performance
 * and allows us to separate the initial 'App' render from subsequent updates, which a lot of our data loading and updating relies on.
 * @param {Boolean} isSSR - True if this is being called on the server.
 * @returns {Array} - An array of full Route components for each route used by the app.
 */
export const getClientRoutes = (isSSR = false) => {
    const schema = pageContext.getProps().allPages;
    return schema.map((pageSchema) => {
        const props = {
            key: pageSchema.name,
            isSSR,
            pageSchema
        };
        const { redirect } = props.pageSchema;
        //
        return (
            <Route
                key="AppRoute"
                exact
                path={pageSchema.path}
                render={(routeProps) => {
                    if (redirect) {
                        return <Redirect to={redirect} />;
                    }
                    // Use the same key here to avoid remounting PageWrapper on route changes
                    return <PageWrapper {...props} {...routeProps} key="PageWrapper" />;
                }}
            />
        );
    });
};

/**
 * Get a set of routes for the server side render. These routes are used within a StaticRouter and are only used for
 * route matching and the initial component render.
 * @returns {Array} - A flat array of simple route objects.
 */
export const getServerRoutes = () => {
    const schema = pageContext.getProps().allPages;
    return schema.map((pageSchema) => {
        return {
            exact: true,
            path: pageSchema.path,
            redirect: pageSchema.redirect,
            component: PageWrapper
        };
    });
};
